import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FilterableMultiSelect, DismissibleTag } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import type { TagResponse } from "../../../types";
import { useGetTags } from "../../../hooks/useTags";
import TagForm from "../../../containers/Tags/TagForm";
import "./tagPicker.scss";

type TagPickerProps = {
  selectedTags: TagResponse[];
  onChange: (tags: TagResponse[]) => void;
  /** Visible field label, rendered as the multi-select's own `titleText`. */
  label: string;
  allowCreate?: boolean;
  maxTags?: number;
  disabled?: boolean;
};

/**
 * The full-Carbon tag picker for the material create/edit forms — replaces the pre-Carbon
 * `components/UI/TagSelector` (a 349-line hand-rolled Tailwind portal popover with its own
 * click-outside/reposition logic and an inline create-tag form).
 *
 * Selecting existing tags is Carbon's `FilterableMultiSelect` — the same control
 * `containers/Materials/MaterialListFilters.tsx` already uses for its tag filter — with each
 * option and each selected chip showing the tag's own colour as a small swatch (colour is DATA
 * here, arbitrary and user-chosen per tag, so it rides on a neutral Carbon chip rather than
 * fighting Carbon's fixed `Tag` palette — the same reasoning `containers/Tags/Tags.tsx`'s swatch
 * cell documents).
 *
 * Creating a tag reuses the existing `containers/Tags/TagForm` dialog (Carbon `ComposedModal` +
 * RHF/Yup + `ColorField`) rather than re-implementing one — its optional `onCreated` callback
 * hands the freshly created tag straight back here to auto-select it.
 *
 * Controlled-selection gotcha: Carbon matches `selectedItems` against `items` by object identity,
 * and the parent's `selectedTags` (seeded from `material.tags`) are NOT the same references as the
 * `useGetTags` list. `items` therefore merges both by id, and `selectedItems` is resolved back out
 * of that merged list — which also keeps a just-created tag selected during the brief window before
 * `useGetTags` refetches it.
 */
const TagPicker = ({
  selectedTags,
  onChange,
  label,
  allowCreate = true,
  maxTags = 10,
  disabled = false,
}: TagPickerProps) => {
  const { t } = useTranslation();
  const { data: allTags = [] } = useGetTags();
  const [isFormOpen, setIsFormOpen] = useState(false);

  const selectedIds = useMemo(
    () => new Set(selectedTags.map((tag) => tag.id)),
    [selectedTags]
  );

  // Merge the fetched list with whatever is already selected (a selected tag may not be in the
  // fetched page yet, e.g. one just created), de-duped by id and sorted by name.
  const items = useMemo(() => {
    const byId = new Map<string, TagResponse>();
    allTags.forEach((tag) => byId.set(tag.id, tag));
    selectedTags.forEach((tag) => {
      if (!byId.has(tag.id)) byId.set(tag.id, tag);
    });
    return Array.from(byId.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [allTags, selectedTags]);

  // Resolve selection back out of `items` so the control gets the exact object references it
  // rendered — the identity Carbon needs to show them as checked.
  const selectedItems = useMemo(
    () => items.filter((tag) => selectedIds.has(tag.id)),
    [items, selectedIds]
  );

  const atMax = selectedTags.length >= maxTags;

  const swatch = (color: string) =>
    function ColorSwatch(props: React.SVGProps<SVGSVGElement>) {
      return (
        <svg
          width={12}
          height={12}
          viewBox="0 0 12 12"
          aria-hidden="true"
          {...props}
        >
          <circle cx="6" cy="6" r="5" fill={color} />
        </svg>
      );
    };

  const handleSelectChange = (next: TagResponse[]) => {
    // Additions past the cap are rejected; removals (which only shrink the set) always pass.
    if (next.length <= maxTags) onChange(next);
  };

  const handleRemove = (tagId: string) =>
    onChange(selectedTags.filter((tag) => tag.id !== tagId));

  const handleCreated = (createdTag: TagResponse) => {
    if (!atMax) onChange([...selectedTags, createdTag]);
  };

  return (
    <div className="tag-picker">
      <div className="tag-picker__controls">
        <FilterableMultiSelect
          id="material-tag-picker"
          className="tag-picker__select"
          size="lg"
          titleText={label}
          placeholder={t("tags.searchOrCreate")}
          items={items}
          itemToString={(tag) => tag?.name ?? ""}
          itemToElement={(tag) =>
            tag ? (
              <span className="tag-picker__option">
                <span
                  className="tag-picker__option-dot"
                  style={{ backgroundColor: tag.color }}
                  aria-hidden="true"
                />
                {tag.name}
              </span>
            ) : (
              <span />
            )
          }
          selectedItems={selectedItems}
          onChange={({ selectedItems: next }) =>
            handleSelectChange(next ?? [])
          }
          disabled={disabled}
        />

        {allowCreate && (
          <Button
            kind="tertiary"
            renderIcon={Add}
            onClick={() => setIsFormOpen(true)}
            disabled={disabled || atMax}
            className="tag-picker__create"
          >
            {t("tags.createNew")}
          </Button>
        )}
      </div>

      {selectedTags.length > 0 && (
        <div className="tag-picker__selected">
          {selectedTags.map((tag) => (
            <DismissibleTag
              key={tag.id}
              type="gray"
              size="md"
              text={tag.name}
              renderIcon={swatch(tag.color)}
              onClose={() => handleRemove(tag.id)}
              title={t("cta.remove")}
            />
          ))}
        </div>
      )}

      {atMax && (
        <p className="tag-picker__max">
          {t("tags.maxTagsReached", { count: maxTags })}
        </p>
      )}

      {allowCreate && (
        <TagForm
          tag={null}
          open={isFormOpen}
          setOpen={setIsFormOpen}
          onCreated={handleCreated}
        />
      )}
    </div>
  );
};

export default TagPicker;
